import React, { PureComponent } from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import moment from 'moment';
import { ConnectedEpisodeListeners } from '../EpisodeListeners';
import { ConnectedEpisodePerformance } from '../EpisodePerformance';
import { ConnectedEpisodePageWithDataAlert } from '../EpisodePageWithDataAlert';
import {
  LOADING,
  ERROR,
  UNAUTHORIZED,
  selectEpisodeMetadata,
  selectEpisodeMetadataStatus,
  selectDatesAvailable,
  selectMe,
} from '../../../store/selectors';
import { updateDateRange } from '../../../store/updateDateRange';
import { loadEpisodeMetadata } from '../../../store/episodeMetadata';
import { requestCsv } from '../../../store/downloadCsv';
import { Helmet } from 'react-helmet';
/* eslint-disable import/no-named-as-default */
import { Header } from '../../../components/Shared/Header';
import { ConnectedDateRangeSelector } from '../../../components/Shared/DateRangeSelector';
import { ConnectedEpisodeStartsStreamsContainer } from '../EpisodeStartsStreamsContainer';
import { ConnectedEpisodeDemographics } from '../EpisodeDemographics';
import { EpisodeMetadata, DataRange, Me, ReduxState } from '../../../types';
import { injectIntl, IntlShape, defineMessage } from 'react-intl';
/* eslint-enable import/no-named-as-default */

type Props = {
  episodeId: string;

  status?: 'LOADING' | 'ERROR' | 'UNAUTHORIZED';
  me: Me;
  datesAvailable: DataRange;
  metadata?: EpisodeMetadata;

  loadEpisodeMetadata: typeof loadEpisodeMetadata;
  updateDateRange: typeof updateDateRange;
  requestCsv: typeof requestCsv;
  intl: IntlShape;
};

class EpisodeLandingPage extends PureComponent<Props> {
  componentDidMount() {
    this.props.loadEpisodeMetadata(this.props.episodeId);
    const endDate = this.props.datesAvailable.until;
    this.props.updateDateRange(
      moment(endDate)
        .subtract(6, 'days')
        .format('YYYY-MM-DD'),
      moment(endDate).format('YYYY-MM-DD'),
      this.props.intl.formatMessage(
        defineMessage({ id: 'episode.default-date-range', defaultMessage: 'Last 7 days' }),
      ),
    );
  }

  get headerProps() {
    const me = this.props.me!;
    const metadata = this.props.metadata!;

    /* istanbul ignore next */
    return {
      artworkUrl: metadata.artworkUrl,
      title: metadata.name,
      type: 'episode',
      metrics: {
        starts: metadata.starts,
        streams: metadata.streams,
        listeners: metadata.listeners,
      },
      metadata: {
        releaseDate: metadata.releaseDate,
        duration: metadata.duration,
      },
      navigation: {
        podcast: metadata.show,
        network: !me.permissions.network ? {} : metadata.network,
      },
    };
  }

  render() {
    const { metadata, status, episodeId } = this.props;

    switch (status) {
      case LOADING:
        return <span data-qa="episode-overview-loading" />;
      case ERROR:
        return <span data-qa="episode-overview-error" />;
      case UNAUTHORIZED:
        return <Redirect to="/no-access" />;
      default:
        break;
    }

    if (!metadata) return null;

    return (
      <div data-qa="episode-landing-page-container">
        <Helmet title={`${metadata.name} - Episode - ${metadata.show.name}`} />
        <Header {...this.headerProps} />
        <ConnectedEpisodePageWithDataAlert>
          <ConnectedDateRangeSelector
            overview
            downloadDataAsCsv={
              /* istanbul ignore next */ () => {
                this.props.requestCsv('episode', [
                  'starts',
                  'streams',
                  'listeners',
                  'followers',
                  'age',
                  'gender',
                  'countries',
                ]);
              }
            }
          />
          <ConnectedEpisodePerformance data-qa="episode-performance" episodeId={episodeId} />
          <ConnectedEpisodeStartsStreamsContainer
            data-qa="episode-starts-streams"
            episodeId={episodeId}
          />
          <ConnectedEpisodeListeners data-qa="episode-listeners" episodeId={episodeId} />
          <ConnectedEpisodeDemographics data-qa="episode-demographics" episodeId={episodeId} />
        </ConnectedEpisodePageWithDataAlert>
      </div>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    status: selectEpisodeMetadataStatus(state),
    metadata: selectEpisodeMetadata(state),
    datesAvailable: selectDatesAvailable(state),
    me: selectMe(state)!,
  };
}

const actions = { loadEpisodeMetadata, updateDateRange, requestCsv };

export const ConnectedEpisodeLandingPage = connect(
  mapStateToProps,
  actions,
)(injectIntl(EpisodeLandingPage));
